import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  useTheme,
} from '@mui/material';
import { Customer } from '../../types/customer';
import { Pencil, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';

interface CustomerDetailProps {
  customer: Customer;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const DetailField = ({
  label,
  value,
  chip = false,
}: {
  label: string;
  value: string | number | boolean | null;
  chip?: boolean;
}) => {
  const theme = useTheme();

  if (chip && typeof value === 'boolean') {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Chip
          label={value ? 'Yes' : 'No'}
          size="small"
          color={value ? 'success' : 'default'}
          sx={{
            fontSize: '0.75rem',
            height: 24,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
        {value || 'Not provided'}
      </Typography>
    </Box>
  );
};

const CustomerDetail: React.FC<CustomerDetailProps> = ({ customer, onClose, onEdit, onDelete }) => {
  const theme = useTheme();

  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Customer Details</Typography>
          <Box>
            <Button startIcon={<Pencil size={16} />} onClick={onEdit} sx={{ mr: 1 }}>
              Edit
            </Button>
            <Button startIcon={<Trash2 size={16} />} color="error" onClick={onDelete}>
              Delete
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailField label="First Name" value={customer.firstName} />
                <DetailField label="Last Name" value={customer.lastName} />
                <DetailField label="Email" value={customer.email} />
                <DetailField label="Phone" value={customer.phone} />
                <DetailField label="Date of Birth" value={customer.dob} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="Created At" value={formatDate(customer.createdAt)} />
                <DetailField label="Last Updated" value={formatDate(customer.updatedAt)} />
                <DetailField
                  label="Allow Extra Time"
                  value={customer.allowExtraTime || false}
                  chip
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
              Address Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailField label="Street Address 1" value={customer.streetOne} />
                <DetailField label="Street Address 2" value={customer.streetTwo} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="City" value={customer.city} />
                <DetailField label="State" value={customer.state} />
                <DetailField label="Postal Code" value={customer.postalCode} />
              </Grid>
            </Grid>
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
              Additional Information
            </Typography>
            <DetailField label="Notes" value={customer.notes} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </>
  );
};

export default CustomerDetail;
